/**
 * Settings Tab � language, timezone, Google Calendar, profile, notifications.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Switch,
  Alert,
  Linking,
  ActivityIndicator,
  Platform,
  KeyboardAvoidingView,
  Modal,
  FlatList,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../../src/stores/authStore';
import { useSettingsStore } from '../../src/stores/settingsStore';
import { getLanguages, LANG_ENGLISH_NAMES, APP_VERSION, getApiBaseUrl } from '../../src/utils/constants';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../src/utils/design-tokens';
import { createPaymentCheckoutIntent, getSubscriptionBundles, updateSettings, getSubscriptionDetails, setAutoRenew, cancelSubscription, extendWithSavedCard, removeCard } from '../../src/api/client';
import type { PaymentProviderStatus, SubscriptionBundle, SubscriptionDetails, User } from '../../src/types/api';
import { getNotifPrefs, setNotifSound, setNotifVibration } from '../../src/utils/notifications';
import { useNotificationHistory } from '../../src/stores/notificationHistoryStore';
import { useNavigationStore } from '../../src/stores/navigationStore';
import { getDevices, logoutOtherDevices } from '../../src/api/client';
import { isBiometricAvailable, getBiometricType, isBiometricEnabled, setBiometricEnabled } from '../../src/utils/biometric';
import LanguagePickerModal from '../../src/components/LanguagePickerModal';
import WhishMark from '../../src/components/payments/WhishMark';
import { useBackHandler } from '../../src/navigation/useBackHandler';

export default function SettingsScreen() {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const refreshProfile = useAuthStore((s) => s.refreshProfile);

  const {
    isUpdating,
    googleConnected,
    googleEmail,
    changeUiLanguage,
    changeServerLanguage,
    changeTimezone,
    detectTimezone,
    toggleAutosync,
    toggleAutoDoneOnFire,
    toggleFollowup,
    setFollowupMinutes,
    checkGoogleStatus,
    getGoogleAuthUrl,
    disconnectGoogle,
    syncAllGoogle,
    importGoogleEvents,
    refreshGoogleSyncCounts,
    googleSyncCounts,
    updateDisplayName,
  } = useSettingsStore();

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user?.displayName || '');
  const [timezoneInput, setTimezoneInput] = useState(user?.tz || user?.timezone || '');
  const [editingTimezone, setEditingTimezone] = useState(false);
  const [detectingTimezone, setDetectingTimezone] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [autoDoneOnFire, setAutoDoneOnFire] = useState(true);
  const [followupEnabled, setFollowupEnabled] = useState(true);
  const [followupMinutes, setFollowupMinutesLocal] = useState(60);
  const [defaultDuration, setDefaultDuration] = useState(user?.defaultDurationMinutes ?? 30);
  const [defaultReminderTime, setDefaultReminderTime] = useState(user?.defaultReminderTimeMinutes ?? 540);
  const initialDefaultTimeInput = useMemo(() => getTimeInputParts(user?.defaultReminderTimeMinutes ?? 540), []);
  const [defaultReminderHourInput, setDefaultReminderHourInput] = useState(initialDefaultTimeInput.hour);
  const [defaultReminderMinuteInput, setDefaultReminderMinuteInput] = useState(initialDefaultTimeInput.minute);
  const [defaultReminderPeriod, setDefaultReminderPeriod] = useState<'AM' | 'PM'>(initialDefaultTimeInput.period);
  const [savingDefaultReminderTime, setSavingDefaultReminderTime] = useState(false);
  const [notifSound, setNotifSoundLocal] = useState(true);
  const [notifVibration, setNotifVibrationLocal] = useState(true);
  const [notifPermission, setNotifPermission] = useState<'granted' | 'denied' | 'undetermined'>('granted');
  const [showNotifHistory, setShowNotifHistory] = useState(false);
  const [showUiLangPicker, setShowUiLangPicker] = useState(false);
  const [showServerLangPicker, setShowServerLangPicker] = useState(false);
  const [subscriptionBundles, setSubscriptionBundles] = useState<SubscriptionBundle[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentProviderStatus | null>(null);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [paymentLoadFailed, setPaymentLoadFailed] = useState(false);
  const [startingBundleId, setStartingBundleId] = useState<number | null>(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [extendChoiceBundle, setExtendChoiceBundle] = useState<SubscriptionBundle | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    danger?: boolean;
    onConfirm: () => void;
    // Optional second action (e.g. 3-way Google disconnect: keep vs remove).
    secondaryLabel?: string;
    onSecondary?: () => void;
    secondaryDanger?: boolean;
  } | null>(null);
  const [autoRenewBusy, setAutoRenewBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [removeCardBusy, setRemoveCardBusy] = useState(false);
  const notifHistoryNotifications = useNotificationHistory(s => s.notifications);
  const notifHistoryUnread = useNotificationHistory(s => s.unreadCount());
  const updateUser = useAuthStore((s) => s.updateUser);
  const { colors, colorScheme, setColorScheme } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const insets = useSafeAreaInsets();
  const tabBarBottomSpace = useNavigationStore((state) => state.tabBarBottomSpace);
  const tabBarKeyboardVisible = useNavigationStore((state) => state.tabBarKeyboardVisible);
  const reservedBottomSpace = tabBarBottomSpace > 0 ? tabBarBottomSpace : 46 + Math.max(insets.bottom, 6);
  const bottomChromeSpace = tabBarKeyboardVisible ? 0 : reservedBottomSpace;

  useBackHandler({
    id: 'settings-tab',
    label: 'Settings',
    priority: 900,
    enabled: showNotifHistory || !!confirmModal || !!extendChoiceBundle || showServerLangPicker || showUiLangPicker || editingTimezone || editingName,
    onBack: () => {
      if (showNotifHistory) {
        setShowNotifHistory(false);
        return true;
      }
      if (confirmModal) {
        setConfirmModal(null);
        return true;
      }
      if (extendChoiceBundle) {
        setExtendChoiceBundle(null);
        return true;
      }
      if (showServerLangPicker) {
        setShowServerLangPicker(false);
        return true;
      }
      if (showUiLangPicker) {
        setShowUiLangPicker(false);
        return true;
      }
      if (editingTimezone) {
        setEditingTimezone(false);
        return true;
      }
      if (editingName) {
        setEditingName(false);
        return true;
      }
      return false;
    },
  });

  useEffect(() => {
    checkGoogleStatus();
    // Load auto-done preference from server
    import('../../src/api/client').then(({ getPreferences }) =>
      getPreferences().then((p) => {
        setAutoDoneOnFire(!!p.auto_done_on_fire);
        setFollowupEnabled(!!p.followup_enabled);
        setFollowupMinutesLocal(p.followup_interval_minutes || 60);
      }).catch(() => {})
    );
    // Load notification sound/vibration prefs
    const prefs = getNotifPrefs();
    setNotifSoundLocal(prefs.sound);
    setNotifVibrationLocal(prefs.vibration);
    // Check notification permission
    if (Platform.OS !== 'web') {
      import('expo-notifications').then(Notif =>
        Notif.getPermissionsAsync().then(({ status }) => setNotifPermission(status as 'granted' | 'denied' | 'undetermined')).catch(() => {})
      );
    }
    setLoadingBundles(true);
    setPaymentLoadFailed(false);
    getSubscriptionBundles()
      .then((result) => {
        setSubscriptionBundles(result.bundles || []);
        setPaymentStatus(result.payments || null);
      })
      .catch(() => setPaymentLoadFailed(true))
      .finally(() => setLoadingBundles(false));
    loadSubscriptionDetails();
  }, []);

  const loadSubscriptionDetails = async () => {
    try {
      const result = await getSubscriptionDetails();
      setSubscriptionDetails(result.subscription || null);
    } catch {
      // Best-effort — auto-renew controls simply stay hidden if this fails.
    }
  };

  // Keep nameInput in sync if user changes
  useEffect(() => {
    setNameInput(user?.displayName || '');
    setTimezoneInput(user?.tz || user?.timezone || '');
    setDefaultDuration(user?.defaultDurationMinutes ?? 30);
    const nextDefaultReminderTime = user?.defaultReminderTimeMinutes ?? 540;
    setDefaultReminderTime(nextDefaultReminderTime);
    const nextTimeInput = getTimeInputParts(nextDefaultReminderTime);
    setDefaultReminderHourInput(nextTimeInput.hour);
    setDefaultReminderMinuteInput(nextTimeInput.minute);
    setDefaultReminderPeriod(nextTimeInput.period);
    setEditingTimezone(false);
  }, [user?.displayName, user?.tz, user?.timezone, user?.defaultDurationMinutes, user?.defaultReminderTimeMinutes]);

  const handleSaveDefaultReminderTime = async () => {
    const parsedMinutes = parseTimeInput(defaultReminderHourInput, defaultReminderMinuteInput, defaultReminderPeriod);
    if (parsedMinutes == null) {
      showAlert(t('settings.defaultReminderTime', 'Default Reminder Time'), t('settings.invalidDefaultReminderTime', 'Enter a valid time.'));
      return;
    }

    const previousDefaultReminderTime = defaultReminderTime;
    setSavingDefaultReminderTime(true);
    setDefaultReminderTime(parsedMinutes);
    try {
      const { user: savedUser } = await updateSettings({ defaultReminderTimeMinutes: parsedMinutes });
      updateUser(savedUser);
      const savedMinutes = savedUser?.defaultReminderTimeMinutes ?? parsedMinutes;
      const savedInput = getTimeInputParts(savedMinutes);
      setDefaultReminderTime(savedMinutes);
      setDefaultReminderHourInput(savedInput.hour);
      setDefaultReminderMinuteInput(savedInput.minute);
      setDefaultReminderPeriod(savedInput.period);
    } catch {
      const previousInput = getTimeInputParts(previousDefaultReminderTime);
      setDefaultReminderTime(previousDefaultReminderTime);
      setDefaultReminderHourInput(previousInput.hour);
      setDefaultReminderMinuteInput(previousInput.minute);
      setDefaultReminderPeriod(previousInput.period);
      showAlert(t('auth.error'), t('settings.defaultReminderTimeSaveFailed', 'Could not save default reminder time.'));
    } finally {
      setSavingDefaultReminderTime(false);
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (trimmed && trimmed !== user?.displayName) {
      setSavingName(true);
      try { await updateDisplayName(trimmed); } finally { setSavingName(false); }
    }
    setEditingName(false);
  };

  const handleUiLanguageChange = async (lang: string) => {
    await changeUiLanguage(lang);
  };

  const handleServerLanguageChange = async (lang: string) => {
    await changeServerLanguage(lang);
  };

  // Web-safe alert wrapper (Alert.alert is a no-op on RN Web)
  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const handleSaveTimezone = async () => {
    const trimmed = timezoneInput.trim();
    if (!trimmed) return;
    // Validate against IANA timezone list
    try {
      Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    } catch {
      showAlert(t('settings.timezone'), t('settings.invalidTimezone', 'Invalid timezone. Use IANA format like "America/New_York".'));
      return;
    }
    await changeTimezone(trimmed);
    setEditingTimezone(false);
    showAlert(t('settings.timezone'), t('settings.timezoneSaved'));
  };

  const handleDetectTimezone = async () => {
    setDetectingTimezone(true);
    try {
      const result = await detectTimezone(33.8938, 35.5018);
      setTimezoneInput(result.tz);
      setEditingTimezone(true);
    } catch {
      showAlert(t('auth.error'), t('settings.detectFailed'));
    } finally {
      setDetectingTimezone(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const url = await getGoogleAuthUrl();
      Linking.openURL(url);
    } catch {
      showAlert(t('common.error'), t('settings.googleConnectFailed'));
    }
  };

  const whishEnabled = !!paymentStatus?.whish?.enabled;

  const handleStartCheckout = async (bundleId: number) => {
    if (!paymentStatus?.enabled) {
      Linking.openURL('https://app.fishmemory.net/subscribe');
      return;
    }
    setStartingBundleId(bundleId);
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // The user already picked Card in the modal, so go straight to
        // /pay/card and skip the redundant /pay chooser.
        const storage = await import('../../src/utils/storage');
        const accessToken = await storage.getAccessToken();
        if (accessToken) {
          try { window.sessionStorage.setItem('fm-access-token', accessToken); } catch { /* ignore */ }
        }
        window.location.assign(`/pay/card?bundleId=${encodeURIComponent(String(bundleId))}`);
        return;
      }

      // Native: mint a short-lived public checkout intent and open /pay in an
      // in-app browser. The page redirects back to fishmemory://checkout-return
      // when the payment completes (or the user cancels), which closes the
      // browser and returns control to the app.
      const intent = await createPaymentCheckoutIntent(bundleId);
      const WebBrowser = await import('expo-web-browser');
      const Linking2 = await import('expo-linking');
      const returnUrl = Linking2.createURL('checkout-return');
      const checkoutUrl = withQueryParams(intent.checkoutUrl, { returnUrl });
      const result = await WebBrowser.openAuthSessionAsync(checkoutUrl, returnUrl, {
        showInRecents: false,
        preferEphemeralSession: false,
      });
      // Always refresh the profile when control returns — even on dismiss —
      // because the payment may have completed before the user closed the tab.
      try { await refreshProfile(); } catch { /* ignore */ }
      if (result.type === 'success' && result.url && result.url.indexOf('status=ok') >= 0) {
        showAlert(
          t('settings.paymentSuccess', 'Payment successful'),
          t('settings.paymentSuccessDesc', 'Your subscription is now active.'),
        );
      }
    } catch {
      showAlert(t('common.error'), t('settings.paymentStartFailed', 'Could not start checkout. Please try again.'));
    } finally {
      setStartingBundleId(null);
    }
  };

  // Tapping a plan: open the chooser modal whenever the user has more than
  // one real option (saved card / new card / Whish wallet). With no Whish
  // and no saved card, there is only "new card", so skip the modal.
  const handlePlanPress = (bundle: SubscriptionBundle) => {
    const hasSavedCard = !!subscriptionDetails?.card;
    if ((paymentStatus?.enabled && hasSavedCard) || whishEnabled) {
      setExtendChoiceBundle(bundle);
      return;
    }
    handleStartCheckout(bundle.id);
  };

  const handleStartWhish = async (bundle: SubscriptionBundle) => {
    setExtendChoiceBundle(null);
    setStartingBundleId(bundle.id);
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Direct hop to /pay/whish - the page reads fm_access_token from
        // localStorage and POSTs /whish/start-authed with a Bearer header.
        // returnUrl points back to Settings so the "Return to FishMemory"
        // button on success closes the loop cleanly.
        const returnUrl = window.location.origin + '/settings';
        const params = new URLSearchParams({
          bundleId: String(bundle.id),
          returnUrl,
        });
        window.location.assign('/pay/whish?' + params.toString());
        return;
      }
      // Native: open /pay/whish in an in-app browser. The redirect back to
      // fishmemory://checkout-return on completion closes the browser.
      const { getApiBaseUrl } = await import('../../src/utils/constants');
      const baseUrl = getApiBaseUrl() || 'https://app.fishmemory.net';
      const WebBrowser = await import('expo-web-browser');
      const Linking2 = await import('expo-linking');
      const returnUrl = Linking2.createURL('checkout-return');
      const whishUrl = withQueryParams(`${baseUrl}/pay/whish`, {
        bundleId: String(bundle.id),
        returnUrl,
      });
      const result = await WebBrowser.openAuthSessionAsync(whishUrl, returnUrl, {
        showInRecents: false,
        preferEphemeralSession: false,
      });
      try { await refreshProfile(); } catch { /* ignore */ }
      if (result.type === 'success' && result.url && result.url.indexOf('status=ok') >= 0) {
        showAlert(
          t('settings.paymentSuccess', 'Payment successful'),
          t('settings.paymentSuccessDesc', 'Your subscription is now active.'),
        );
      }
    } catch {
      showAlert(t('common.error'), t('settings.paymentStartFailed', 'Could not start checkout. Please try again.'));
    } finally {
      setStartingBundleId(null);
    }
  };

  const handleExtendWithSavedCard = async (bundle: SubscriptionBundle) => {
    setExtendChoiceBundle(null);
    setStartingBundleId(bundle.id);
    try {
      await extendWithSavedCard(bundle.id);
      try { await refreshProfile(); } catch { /* ignore */ }
      await loadSubscriptionDetails();
      showAlert(
        t('settings.paymentSuccess', 'Payment successful'),
        t('settings.paymentSuccessDesc', 'Your subscription is now active.'),
      );
    } catch {
      showAlert(
        t('common.error'),
        t('settings.extendSavedCardFailed', 'We could not charge your saved card. Please try a different card.'),
      );
    } finally {
      setStartingBundleId(null);
    }
  };

  const applyAutoRenew = async (enabled: boolean) => {
    setAutoRenewBusy(true);
    try {
      const result = await setAutoRenew(enabled);
      setSubscriptionDetails(result.subscription);
    } catch (error: any) {
      const code = error?.response?.data?.error;
      if (code === 'no_payment_instrument') {
        showAlert(
          t('settings.autoRenew', 'Auto-renew'),
          t('settings.autoRenewNoCard', 'Add a card on file before enabling auto-renew.'),
        );
      } else if (code === 'subscription_inactive') {
        showAlert(
          t('settings.autoRenew', 'Auto-renew'),
          t('settings.autoRenewInactive', 'Start a subscription before enabling auto-renew.'),
        );
      } else if (code === 'no_renewal_bundle') {
        showAlert(
          t('settings.autoRenew', 'Auto-renew'),
          t('settings.autoRenewNoBundle', 'This plan cannot be auto-renewed.'),
        );
      } else {
        showAlert(
          t('common.error'),
          t('settings.autoRenewFailed', 'Could not update auto-renew. Please try again.'),
        );
      }
    } finally {
      setAutoRenewBusy(false);
    }
  };

  // Branded in-app confirmation — replaces flaky/ugly native window.confirm + Alert.
  const askConfirm = (opts: {
    title: string; message: string; confirmLabel: string; danger?: boolean; onConfirm: () => void;
    secondaryLabel?: string; onSecondary?: () => void; secondaryDanger?: boolean;
  }) => setConfirmModal(opts);

  const handleToggleAutoRenew = (next: boolean) => {
    if (autoRenewBusy) return;
    if (next) {
      applyAutoRenew(true);
      return;
    }
    // Turning auto-renew OFF — confirm so the user understands access stays until the paid-through date.
    const paidThrough = formatDisplayDate(subscriptionDetails?.subUntilUtc) || t('settings.notAvailable', 'Not available');
    askConfirm({
      title: t('settings.autoRenewOffTitle', 'Turn off auto-renew?'),
      message: t('settings.autoRenewOffConfirm', {
        defaultValue: "You'll keep access until {{date}}.",
        date: paidThrough,
      }),
      confirmLabel: t('settings.autoRenewOffConfirmAction', 'Turn off'),
      danger: true,
      onConfirm: () => applyAutoRenew(false),
    });
  };

  const performCancelSubscription = async () => {
    setCancelBusy(true);
    try {
      const result = await cancelSubscription();
      setSubscriptionDetails(result.subscription);
      const paidThrough = formatDisplayDate(result.subUntilUtc);
      showAlert(
        t('settings.cancelSubscription', 'Cancel subscription'),
        paidThrough
          ? t('settings.subscriptionCancelled', {
              defaultValue: 'Auto-renew is off. You keep access until {{date}}.',
              date: paidThrough,
            })
          : t('settings.subscriptionCancelledNoDate', 'Auto-renew is off.'),
      );
    } catch {
      showAlert(t('common.error'), t('settings.cancelSubscriptionFailed', 'Could not cancel subscription. Please try again.'));
    } finally {
      setCancelBusy(false);
    }
  };

  const handleCancelSubscription = () => {
    if (cancelBusy) return;
    const paidThrough = formatDisplayDate(subscriptionDetails?.subUntilUtc) || t('settings.notAvailable', 'Not available');
    askConfirm({
      title: t('settings.cancelSubscription', 'Cancel subscription'),
      message: t('settings.cancelSubscriptionConfirm', {
        defaultValue: "You'll keep access until {{date}}, then your subscription ends.",
        date: paidThrough,
      }),
      confirmLabel: t('settings.cancelSubscriptionAction', 'Cancel subscription'),
      danger: true,
      onConfirm: performCancelSubscription,
    });
  };

  const handleUpdateCard = async () => {
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Hand off to the standalone /pay page in card-update mode.
        const storage = await import('../../src/utils/storage');
        const accessToken = await storage.getAccessToken();
        if (accessToken) {
          try { window.sessionStorage.setItem('fm-access-token', accessToken); } catch { /* ignore */ }
        }
        window.location.assign('/pay?mode=card-update');
        return;
      }

      // Native: open the card-update checkout in an in-app browser.
      const WebBrowser = await import('expo-web-browser');
      const Linking2 = await import('expo-linking');
      const returnUrl = Linking2.createURL('checkout-return');
      const baseUrl = getApiBaseUrl();
      const checkoutUrl = withQueryParams(`${baseUrl}/pay`, { mode: 'card-update', returnUrl });
      await WebBrowser.openAuthSessionAsync(checkoutUrl, returnUrl, {
        showInRecents: false,
        preferEphemeralSession: false,
      });
      // Refresh details when control returns — the card may have been updated.
      await loadSubscriptionDetails();
    } catch {
      showAlert(t('common.error'), t('settings.updateCardFailed', 'Could not start card update. Please try again.'));
    }
  };

  const performRemoveCard = async () => {
    setRemoveCardBusy(true);
    try {
      const result = await removeCard();
      setSubscriptionDetails(result.subscription);
      try { await refreshProfile(); } catch { /* ignore */ }
    } catch {
      showAlert(t('common.error'), t('settings.removeCardFailed', 'Could not remove your card. Please try again.'));
    } finally {
      setRemoveCardBusy(false);
    }
  };

  const handleRemoveCard = () => {
    if (removeCardBusy) return;
    askConfirm({
      title: t('settings.removeCardConfirmTitle', 'Remove card?'),
      message: t('settings.removeCardConfirmMessage', 'Your saved card will be deleted and auto-renew turned off. Your subscription stays active until it expires.'),
      confirmLabel: t('settings.removeCardAction', 'Remove card'),
      danger: true,
      onConfirm: performRemoveCard,
    });
  };

  const handleLogout = () => {
    const doLogout = async () => {
      await logout();
      router.replace('/(auth)/phone');
    };
    if (Platform.OS === 'web') {
      if (window.confirm(t('settings.logoutConfirm'))) doLogout();
    } else {
      Alert.alert(
        t('settings.logoutTitle'),
        t('settings.logoutConfirm'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('settings.logout'), style: 'destructive', onPress: doLogout },
        ]
      );
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const { exportUserData } = await import('../../src/api/client');
      const data = await exportUserData();
      const json = JSON.stringify(data, null, 2);
      if (Platform.OS === 'web') {
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'fishmemory-export.json';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const { Share } = await import('react-native');
        await Share.share({ message: json });
      }
    } catch {
      showAlert(t('common.error'), t('settings.exportFailed', 'Failed to export data'));
    } finally {
      setExportingData(false);
    }
  };

  const [deletingAccount, setDeletingAccount] = useState(false);
  const [activeDevices, setActiveDevices] = useState<{ device_id: string; platform: string; app_version: string; last_seen_utc: string }[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [biometricAvail, setBiometricAvail] = useState(false);
  const [biometricType, setBiometricType] = useState<'face' | 'fingerprint' | 'none'>('none');
  const [biometricOn, setBiometricOn] = useState(false);

  useEffect(() => {
    (async () => {
      const avail = await isBiometricAvailable();
      setBiometricAvail(avail);
      if (avail) {
        setBiometricType(await getBiometricType());
        setBiometricOn(await isBiometricEnabled());
      }
    })();
  }, []);

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const { devices } = await getDevices();
      setActiveDevices(devices || []);
    } catch { /* ignore */ }
    finally { setLoadingDevices(false); }
  };

  const handleLogoutOthers = async () => {
    const deviceId = await import('../../src/utils/storage').then(m => m.getDeviceId());
    if (!deviceId) return;
    const doIt = async () => {
      try {
        await logoutOtherDevices(deviceId);
        showAlert(t('settings.sessions', 'Sessions'), t('settings.logoutOthersSuccess', 'All other devices have been logged out.'));
        loadDevices();
      } catch {
        showAlert(t('common.error'), t('common.tryAgain'));
      }
    };
    if (Platform.OS === 'web') {
      if (window.confirm(t('settings.logoutOthersConfirm', 'Log out all other devices?'))) doIt();
    } else {
      Alert.alert(
        t('settings.sessions', 'Sessions'),
        t('settings.logoutOthersConfirm', 'Log out all other devices?'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('settings.logoutOthers', 'Log Out Others'), style: 'destructive', onPress: doIt },
        ]
      );
    }
  };

  const handleDeleteAccount = () => {
    const doDelete = async () => {
      setDeletingAccount(true);
      try {
        const { deleteAccount } = await import('../../src/api/client');
        const result = await deleteAccount();
        const delDate = result?.scheduled_deletion_at
          ? new Date(result.scheduled_deletion_at).toLocaleDateString()
          : '30 days';
        showAlert(
          t('settings.deleteTitle', 'Delete Account'),
          t('settings.deleteScheduled', 'Your account is scheduled for deletion on {{date}}. You can cancel this from Settings before then. FishMemory support has been notified.', { date: delDate })
        );
      } catch {
        showAlert(t('common.error'), t('settings.deleteFailed', 'Failed to delete account'));
      } finally {
        setDeletingAccount(false);
      }
    };
    if (Platform.OS === 'web') {
      if (window.confirm(t('settings.deleteConfirm', 'This will schedule your account for deletion in 30 days and notify FishMemory support. Continue?'))) doDelete();
    } else {
      Alert.alert(
        t('settings.deleteTitle', 'Delete Account'),
        t('settings.deleteConfirm', 'This will schedule your account for deletion in 30 days and notify FishMemory support. You can cancel anytime before then.'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('settings.deleteButton', 'Delete'), style: 'destructive', onPress: doDelete },
        ]
      );
    }
  };

  const handleCancelDeletion = async () => {
    try {
      const { cancelDeletion } = await import('../../src/api/client');
      await cancelDeletion();
      showAlert(t('settings.deleteTitle', 'Delete Account'), t('settings.deletionCancelled', 'Account deletion has been cancelled. FishMemory support has been notified.'));
    } catch {
      showAlert(t('common.error'), t('settings.cancelDeletionFailed', 'Failed to cancel deletion'));
    }
  };

  const handleFeedback = () => {
    Linking.openURL('mailto:support@fishmemory.net?subject=FishMemory%20Feedback');
  };

  return (
    <KeyboardAvoidingView
      style={[s.container, { paddingTop: insets.top, paddingBottom: bottomChromeSpace }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={0}
    >
      <ScrollView contentContainerStyle={s.content} keyboardShouldPersistTaps="handled">
        {/* Hero Card */}
        <View style={s.heroCard}>
          <View style={s.heroOrbPrimary} />
          <View style={s.heroOrbSecondary} />
          <Text style={s.heroTitle}>{t('tabs.settings')}</Text>
          <Text style={s.heroSubtitle}>{user?.waId || ''}</Text>
        </View>

        {/* Profile section */}
        <SectionHeader title={t('settings.profile')} />
        <View style={s.card}>
          <View style={s.profileRow}>
            <View style={s.avatar}>
              <Text style={s.avatarText}>
                {(user?.displayName || user?.waId || '?')[0].toUpperCase()}
              </Text>
            </View>
            <View style={s.profileInfo}>
              {editingName ? (
                <View style={s.editNameRow}>
                  <TextInput
                    style={s.nameInput}
                    value={nameInput}
                    onChangeText={setNameInput}
                    placeholder={t('settings.enterName')}
                    placeholderTextColor={colors.textSecondary}
                    autoFocus
                    returnKeyType="done"
                    onSubmitEditing={handleSaveName}
                  />
                  <TouchableOpacity style={s.saveNameBtn} onPress={handleSaveName} disabled={savingName} accessibilityRole="button" accessibilityLabel="Save name">
                    {savingName ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="checkmark" size={18} color="#fff" />}
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity onPress={() => setEditingName(true)} style={s.nameRow}>
                  <Text style={s.profileName}>
                    {user?.displayName || t('settings.noName')}
                  </Text>
                  <Ionicons name="pencil-outline" size={16} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
              <Text style={s.profilePhone}>{user?.waId}</Text>
            </View>
          </View>
        </View>

        {/* Appearance */}
        <SectionHeader title={t('settings.appearance', 'Appearance')} />
        <View style={s.card}>
          {(['system', 'light', 'dark'] as const).map((scheme) => {
            const label = scheme === 'system' ? t('settings.themeSystem', 'System')
              : scheme === 'light' ? t('settings.themeLight', 'Light')
              : t('settings.themeDark', 'Dark');
            const icon = scheme === 'system' ? 'phone-portrait-outline'
              : scheme === 'light' ? 'sunny-outline'
              : 'moon-outline';
            const isActive = colorScheme === scheme;
            return (
              <TouchableOpacity
                key={scheme}
                style={[s.themeOption, isActive && { backgroundColor: colors.primaryLight, borderColor: colors.primary }]}
                onPress={() => setColorScheme(scheme)}
                activeOpacity={0.7}
              >
                <Ionicons name={icon as any} size={18} color={isActive ? colors.primary : colors.textSecondary} />
                <Text style={[s.themeOptionText, isActive && { color: colors.primary, fontWeight: '600' }]}>{label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Language */}
        <SectionHeader title={t('settings.language')} />
        <View style={s.card}>
          <TouchableOpacity
            style={s.settingRow}
            onPress={() => setShowUiLangPicker(true)}
            disabled={isUpdating}
            activeOpacity={0.7}
          >
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.uiLanguage')}</Text>
              <Text style={s.settingDesc}>{t('settings.uiLanguageHelp')}</Text>
            </View>
            <View style={s.langTrigger}>
              <Text style={s.langTriggerFlag}>{getLanguages().find(l => l.code === i18n.language)?.flag || '🌍'}</Text>
              <Text style={s.langTriggerLabel}>{getLanguages().find(l => l.code === i18n.language)?.label || i18n.language}</Text>
              <Ionicons name="chevron-forward" size={16} color={colors.textSecondary} />
            </View>
          </TouchableOpacity>

          <View style={s.languageDivider} />

          <TouchableOpacity
            style={s.settingRow}
            onPress={() => setShowServerLangPicker(true)}
            disabled={isUpdating}
            activeOpacity={0.7}
          >
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.serverLanguage')}</Text>
              <Text style={s.settingDesc}>{t('settings.serverLanguageHelp')}</Text>
            </View>
            <View style={s.langTrigger}>
              <Text style={s.langTriggerFlag}>{getLanguages().find(l => l.code === user?.lang)?.flag || '🌍'}</Text>
              <Text style={s.langTriggerLabel}>{getLanguages().find(l => l.code === user?.lang)?.label || user?.lang || 'en'}</Text>
              <Ionicons name="chevron-forward" size={16} color={colors.textSecondary} />
            </View>
          </TouchableOpacity>
        </View>

        <LanguagePickerModal
          visible={showUiLangPicker}
          onClose={() => setShowUiLangPicker(false)}
          onSelect={handleUiLanguageChange}
          selectedCode={i18n.language}
          languages={getLanguages()}
          title={t('settings.uiLanguage')}
        />
        <LanguagePickerModal
          visible={showServerLangPicker}
          onClose={() => setShowServerLangPicker(false)}
          onSelect={handleServerLanguageChange}
          selectedCode={user?.lang || 'en'}
          languages={getLanguages()}
          title={t('settings.serverLanguage')}
        />

        {/* Timezone */}
        <SectionHeader title={t('settings.timezone')} />
        <View style={s.card}>
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.currentTimezone')}</Text>
              <Text style={s.settingDesc}>{t('settings.timezoneHelp')}</Text>
            </View>
          </View>
          {!editingTimezone ? (
            <TouchableOpacity
              style={s.readonlyField}
              onPress={() => setEditingTimezone(true)}
              activeOpacity={0.8}
            >
              <View style={s.readonlyFieldTextWrap}>
                <Text style={s.readonlyFieldValue}>{timezoneInput || t('settings.timezonePlaceholder')}</Text>
                <Text style={s.readonlyFieldHint}>{t('common.edit')}</Text>
              </View>
              <Ionicons name="pencil-outline" size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          ) : (
            <View style={s.inputSection}>
              <TextInput
                style={s.nameInput}
                value={timezoneInput}
                onChangeText={setTimezoneInput}
                placeholder={t('settings.timezonePlaceholder')}
                placeholderTextColor={colors.textSecondary}
                autoCapitalize="none"
                autoCorrect={false}
                onSubmitEditing={handleSaveTimezone}
                autoFocus
              />
              <View style={s.actionRow}>
                <TouchableOpacity style={s.secondaryButton} onPress={handleDetectTimezone} disabled={detectingTimezone}>
                  {detectingTimezone ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <Text style={s.secondaryButtonText}>{t('settings.detect')}</Text>
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={s.secondaryButton}
                  onPress={() => {
                    setTimezoneInput(user?.tz || user?.timezone || '');
                    setEditingTimezone(false);
                  }}
                >
                  <Text style={s.secondaryButtonText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={s.connectButton} onPress={handleSaveTimezone}>
                  <Text style={s.connectButtonText}>{t('settings.timezoneSave')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>

        {/* Notifications */}
        <SectionHeader title={t('settings.notifications')} />
        <View style={s.card}>
          {notifPermission !== 'granted' && Platform.OS !== 'web' && (
            <TouchableOpacity
              style={[s.settingRow, { backgroundColor: colors.warning + '33', borderRadius: 8, marginBottom: 8 }]}
              onPress={() => Linking.openSettings()}
            >
              <Text style={{ color: colors.text, fontSize: 13, flex: 1 }}>
                {t('settings.notifDenied', '⚠️ Notifications are disabled. Tap to open settings and enable them.')}
              </Text>
            </TouchableOpacity>
          )}
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingDesc}>{t('settings.notificationsHelp')}</Text>
            </View>
          </View>
          {/* Auto-done on fire toggle */}
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.autoDoneOnFire')}</Text>
              <Text style={s.settingDesc}>{t('settings.autoDoneOnFireDesc')}</Text>
            </View>
            <Switch
              value={autoDoneOnFire}
              onValueChange={(v) => {
                setAutoDoneOnFire(v);
                toggleAutoDoneOnFire(v);
              }}
              trackColor={{ false: colors.border, true: colors.primary }}
            />
          </View>
          {/* Follow-up reminders */}
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.followupEnabled', 'Follow-up reminders')}</Text>
              <Text style={s.settingDesc}>{t('settings.followupEnabledDesc', 'Send a check-in for overdue reminders so you can mark them done, snooze, or edit time.')}</Text>
            </View>
            <Switch
              value={followupEnabled}
              onValueChange={(v) => {
                setFollowupEnabled(v);
                toggleFollowup(v);
              }}
              trackColor={{ false: colors.border, true: colors.primary }}
            />
          </View>
          {followupEnabled && (
            <View style={[s.settingRow, { flexDirection: 'column', alignItems: 'flex-start' }]}>
              <View style={[s.settingInfo, { marginBottom: 8 }]}>
                <Text style={s.settingLabel}>{t('settings.followupInterval', 'Follow-up every')}</Text>
              </View>
              <View style={s.intervalRow}>
                {[15, 30, 60, 120, 240].map((mins) => (
                  <TouchableOpacity
                    key={mins}
                    style={[
                      s.intervalChip,
                      followupMinutes === mins && s.intervalChipActive,
                    ]}
                    onPress={() => {
                      setFollowupMinutesLocal(mins);
                      setFollowupMinutes(mins);
                    }}
                  >
                    <Text
                      style={[
                        s.intervalText,
                        followupMinutes === mins && s.intervalTextActive,
                      ]}
                    >
                      {mins < 60 ? `${mins}m` : `${mins / 60}h`}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}
          {/* Notification sound toggle */}
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.notifSound', 'Notification Sound')}</Text>
            </View>
            <Switch
              value={notifSound}
              onValueChange={(v) => { setNotifSoundLocal(v); setNotifSound(v); }}
              trackColor={{ false: colors.border, true: colors.primary }}
            />
          </View>
          {/* Notification vibration toggle */}
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.notifVibration', 'Vibration')}</Text>
            </View>
            <Switch
              value={notifVibration}
              onValueChange={(v) => { setNotifVibrationLocal(v); setNotifVibration(v); }}
              trackColor={{ false: colors.border, true: colors.primary }}
            />
          </View>
          {/* Test notification */}
          <TouchableOpacity
            style={[s.settingRow, { justifyContent: 'center', paddingVertical: 10 }]}
            onPress={async () => {
              try {
                if (Platform.OS === 'web') {
                  const { showWebNotification, requestWebPermission } = await import('../../src/utils/webNotifications');
                  const granted = await requestWebPermission();
                  if (!granted) throw new Error('Permission denied');
                  showWebNotification('🐟 FishMemory', t('settings.testNotifBody', 'Notifications are working!'));
                } else {
                  const Notif = await import('expo-notifications');
                  await Notif.scheduleNotificationAsync({
                    content: { title: '🐟 FishMemory', body: t('settings.testNotifBody', 'Notifications are working!'), data: {} },
                    trigger: null,
                  });
                }
              } catch {
                showAlert(t('settings.notifications'), t('settings.testNotifFailed', 'Could not send test notification'));
              }
            }}
          >
            <Text style={{ color: colors.primary, fontWeight: '600', fontSize: 14 }}>
              {t('settings.testNotification', '🔔 Send Test Notification')}
            </Text>
          </TouchableOpacity>
          {/* Notification history */}
          <TouchableOpacity
            style={[s.settingRow, { justifyContent: 'space-between' }]}
            onPress={() => setShowNotifHistory(true)}
          >
            <Text style={s.settingLabel}>{t('settings.notifHistory', 'Notification History')}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              {notifHistoryUnread > 0 && (
                <View style={{ backgroundColor: '#FF3B30', borderRadius: 9, minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 }}>
                  <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>{notifHistoryUnread}</Text>
                </View>
              )}
              <Ionicons name="chevron-forward" size={16} color={colors.textSecondary} />
            </View>
          </TouchableOpacity>
        </View>

        {/* Reminders defaults */}
        <SectionHeader title={t('settings.reminders', 'Reminders')} />
        <View style={s.card}>
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.defaultReminderTime', 'Default Reminder Time')}</Text>
              <Text style={s.settingDesc}>{t('settings.defaultReminderTimeHelp', 'Used when a reminder has a date but no specific time, and for calendar quick add.')}</Text>
            </View>
          </View>
          <View style={s.defaultTimeEditorRow}>
            <View style={s.timeInputGroup}>
              <TextInput
                style={s.timeNumberInput}
                value={defaultReminderHourInput}
                onChangeText={(value) => setDefaultReminderHourInput(sanitizeNumberInput(value, 2))}
                onBlur={() => {
                  const hour = Number(defaultReminderHourInput);
                  if (Number.isInteger(hour) && hour >= 1 && hour <= 12) {
                    setDefaultReminderHourInput(String(hour));
                  }
                }}
                keyboardType="number-pad"
                maxLength={2}
                selectTextOnFocus
                accessibilityLabel="Default reminder hour"
              />
              <Text style={s.timeSeparator}>:</Text>
              <TextInput
                style={s.timeNumberInput}
                value={defaultReminderMinuteInput}
                onChangeText={(value) => setDefaultReminderMinuteInput(sanitizeNumberInput(value, 2))}
                onBlur={() => {
                  const minute = Number(defaultReminderMinuteInput);
                  if (Number.isInteger(minute) && minute >= 0 && minute <= 59) {
                    setDefaultReminderMinuteInput(String(minute).padStart(2, '0'));
                  }
                }}
                keyboardType="number-pad"
                maxLength={2}
                selectTextOnFocus
                accessibilityLabel="Default reminder minute"
              />
            </View>
            <View style={s.periodToggle}>
              {(['AM', 'PM'] as const).map((period) => (
                <TouchableOpacity
                  key={period}
                  style={[s.periodOption, defaultReminderPeriod === period && s.periodOptionActive]}
                  onPress={() => setDefaultReminderPeriod(period)}
                  accessibilityRole="button"
                  accessibilityLabel={`Use ${period}`}
                >
                  <Text style={[s.periodText, defaultReminderPeriod === period && s.periodTextActive]}>{period}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity
              style={[s.timeSaveButton, savingDefaultReminderTime && s.timeSaveButtonDisabled]}
              onPress={handleSaveDefaultReminderTime}
              disabled={savingDefaultReminderTime}
              accessibilityRole="button"
              accessibilityLabel="Save default reminder time"
            >
              {savingDefaultReminderTime ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Ionicons name="checkmark" size={18} color="#fff" />
              )}
            </TouchableOpacity>
          </View>
          <View style={s.defaultTimePreviewRow}>
            <Ionicons name="time-outline" size={15} color={colors.textSecondary} />
            <Text style={s.defaultTimePreviewText}>
              {t('settings.defaultReminderTimeCurrent', 'Current default: {{time}}', { time: formatTimeOfDay(defaultReminderTime) })}
            </Text>
          </View>
          <View style={s.settingRow}>
            <View style={s.settingInfo}>
              <Text style={s.settingLabel}>{t('settings.defaultDuration', 'Default Duration')}</Text>
              <Text style={s.settingDesc}>{t('settings.defaultDurationHelp', 'Used for new reminders without explicit duration')}</Text>
            </View>
          </View>
          <View style={[s.settingRow, { flexWrap: 'wrap', gap: 8 }]}>
            {[15, 30, 60, 120].map((d) => (
              <TouchableOpacity
                key={d}
                style={[
                  s.durationChip,
                  defaultDuration === d && s.durationChipActive,
                ]}
                onPress={async () => {
                  setDefaultDuration(d);
                  updateUser({ defaultDurationMinutes: d });
                  try { await updateSettings({ defaultDurationMinutes: d }); } catch {}
                }}
              >
                <Text style={[
                  s.durationChipText,
                  defaultDuration === d && s.durationChipTextActive,
                ]}>
                  {d >= 60 ? `${d / 60}h` : `${d}m`}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Google Calendar */}
        <SectionHeader title={t('settings.googleCalendar')} />
        <View style={s.card}>
          {googleConnected ? (
            <>
              {/* Connected header — green check + account email */}
              <View style={s.settingRow}>
                <View style={s.settingInfo}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Ionicons name="checkmark-circle" size={18} color={colors.success || '#10B981'} />
                    <Text style={[s.settingLabel, { color: colors.success || '#10B981' }]}>
                      {t('settings.googleConnected', 'Connected')}
                    </Text>
                  </View>
                  {googleEmail ? (
                    <Text style={[s.settingDesc, { marginTop: 4, fontWeight: '700', color: colors.text }]}>
                      {googleEmail}
                    </Text>
                  ) : null}
                  {/* X of Y synced status line */}
                  {googleSyncCounts ? (
                    <Text style={[s.settingDesc, { marginTop: 4 }]}>
                      {t('settings.googleSyncStatus', {
                        defaultValue: '{{synced}} of {{total}} reminders are on your Google Calendar.',
                        synced: googleSyncCounts.synced,
                        total: googleSyncCounts.total,
                      })}
                    </Text>
                  ) : null}
                </View>
              </View>

              {/* Auto-sync toggle */}
              <View style={s.settingRow}>
                <View style={[s.settingInfo, { flex: 1, paddingRight: 12 }]}>
                  <Text style={s.settingLabel}>{t('settings.autoSync', 'Auto-sync new reminders')}</Text>
                  <Text style={[s.settingDesc, { marginTop: 2 }]}>
                    {t('settings.googleAutoSyncHelp', 'When on, every new reminder is added to your Google Calendar automatically.')}
                  </Text>
                </View>
                <Switch
                  value={user?.autosyncGoogle || false}
                  onValueChange={toggleAutosync}
                  trackColor={{ false: colors.border, true: colors.primary }}
                />
              </View>

              {/* Sync all existing reminders now */}
              {googleSyncCounts && googleSyncCounts.unsynced > 0 ? (
                <View style={s.settingRow}>
                  <View style={[s.settingInfo, { flex: 1, paddingRight: 12 }]}>
                    <Text style={s.settingLabel}>{t('settings.googleSyncAll', 'Sync all reminders now')}</Text>
                    <Text style={[s.settingDesc, { marginTop: 2 }]}>
                      {t('settings.googleSyncAllHelp', 'Add reminders you created before connecting (one-time catch-up).')}
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={[s.connectButton, { paddingHorizontal: 14 }]}
                    disabled={isUpdating}
                    onPress={async () => {
                      const n = await syncAllGoogle();
                      await refreshGoogleSyncCounts();
                      showAlert(
                        t('settings.googleSyncAll', 'Sync all reminders now'),
                        t('settings.googleSyncAllResult', { defaultValue: '{{count}} reminder(s) synced to Google Calendar.', count: n }),
                      );
                    }}
                  >
                    <Text style={s.connectButtonText}>
                      {isUpdating ? t('common.loading', 'Working…') : t('settings.googleSyncAllAction', 'Sync')}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : null}

              {/* Import events FROM Google into FishMemory (two-way) */}
              <View style={s.settingRow}>
                <View style={[s.settingInfo, { flex: 1, paddingRight: 12 }]}>
                  <Text style={s.settingLabel}>{t('settings.googleImport', 'Import from Google')}</Text>
                  <Text style={[s.settingDesc, { marginTop: 2 }]}>
                    {t('settings.googleImportHelp', 'Bring events you created in Google Calendar into FishMemory. They show with a red Google mark.')}
                  </Text>
                </View>
                <TouchableOpacity
                  style={[s.connectButton, { paddingHorizontal: 14 }]}
                  disabled={isUpdating}
                  onPress={async () => {
                    const { imported, updated } = await importGoogleEvents();
                    await refreshGoogleSyncCounts();
                    showAlert(
                      t('settings.googleImport', 'Import from Google'),
                      t('settings.googleImportResult', {
                        defaultValue: '{{imported}} imported, {{updated}} updated.',
                        imported, updated,
                      }),
                    );
                  }}
                >
                  <Text style={s.connectButtonText}>
                    {isUpdating ? t('common.loading', 'Working…') : t('settings.googleImportAction', 'Import')}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Open Google Calendar */}
              <TouchableOpacity
                style={[s.settingRow, { alignItems: 'center' }]}
                onPress={() => {
                  const url = 'https://calendar.google.com/calendar/r';
                  if (Platform.OS === 'web' && typeof window !== 'undefined') window.open(url, '_blank');
                  else Linking.openURL(url).catch(() => {});
                }}
              >
                <Text style={[s.settingLabel, { color: colors.primary }]}>
                  {t('settings.googleOpen', 'Open Google Calendar')}
                </Text>
                <Ionicons name="open-outline" size={18} color={colors.primary} />
              </TouchableOpacity>

              {/* Disconnect — 3-way: keep events / remove events / cancel */}
              <TouchableOpacity
                style={[s.connectButton, { backgroundColor: 'transparent', borderWidth: 1, borderColor: colors.error || '#EF4444', alignSelf: 'flex-start', marginTop: 8 }]}
                onPress={() => {
                  askConfirm({
                    title: t('settings.googleDisconnectTitle', 'Disconnect Google Calendar?'),
                    message: t('settings.googleDisconnectMessage2', 'Your reminders stay safe in FishMemory. What should happen to the events FishMemory added to your Google Calendar?'),
                    confirmLabel: t('settings.googleDisconnectKeep', 'Keep them on Google'),
                    onConfirm: () => { disconnectGoogle('keep'); },
                    secondaryLabel: t('settings.googleDisconnectRemove', 'Remove them from Google'),
                    secondaryDanger: true,
                    onSecondary: () => { disconnectGoogle('remove'); },
                  });
                }}
                disabled={isUpdating}
              >
                <Text style={[s.connectButtonText, { color: colors.error || '#EF4444' }]}>
                  {t('settings.disconnect', 'Disconnect')}
                </Text>
              </TouchableOpacity>
            </>
          ) : (
            <View style={s.settingRow}>
              <View style={s.settingInfo}>
                <Text style={s.settingLabel}>{t('settings.googleCalendar')}</Text>
                <Text style={s.settingDesc}>{t('settings.googleDisconnectedHelp')}</Text>
              </View>
              <TouchableOpacity
                style={s.connectButton}
                onPress={handleConnectGoogle}
              >
                <Text style={s.connectButtonText}>{t('settings.connect')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Subscription */}
        <SectionHeader title={t('settings.subscription')} />
        <View style={s.card}>
          {(() => {
            const summary = getSubscriptionSummary(user, t, colors);
            const cardEnabled = !!paymentStatus?.enabled;
            const gateway = getGatewaySummary(paymentStatus, paymentLoadFailed, whishEnabled, t);
            const basePlanAction = user?.subStatus === 'active'
              ? t('settings.extendPlan', 'Extend')
              : user?.subStatus === 'expired'
                ? t('settings.renewPlan', 'Renew')
                : t('settings.upgradePlan', 'Upgrade');
            const visiblePaymentMethodCount = Number(cardEnabled) + Number(whishEnabled);
            const planAction = getPlanActionLabel(basePlanAction, cardEnabled, whishEnabled, t);
            const hasManagement = !!subscriptionDetails
              && (subscriptionDetails.subStatus === 'active' || !!subscriptionDetails.card);

            return (
              <>
                {/* Status header */}
                <View style={s.subStatusHeader}>
                  <View style={[s.subStatusIcon, { backgroundColor: summary.iconBg }]}>
                    <Ionicons name={summary.icon as any} size={22} color={summary.iconColor} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={s.subStatusTitle}>{summary.title}</Text>
                    <Text style={s.subStatusSubtitle}>{summary.description}</Text>
                  </View>
                  <View style={[s.statusPill, { backgroundColor: summary.pillBg, borderColor: summary.pillBorder }]}>
                    <Text style={[s.statusPillText, { color: summary.pillText }]}>{summary.statusLabel}</Text>
                  </View>
                </View>

                {subscriptionDetails?.renewalStatus === 'past_due' && (
                  <View style={s.renewalBanner}>
                    <Ionicons name="alert-circle" size={18} color={colors.error} />
                    <Text style={s.renewalBannerText}>
                      {t('settings.renewalPastDue', "We couldn't renew your subscription — update your card.")}
                    </Text>
                  </View>
                )}

                {/* Plans */}
                <View style={s.subGroup}>
                  <View style={s.planHeaderRow}>
                    <Text style={s.subGroupLabel}>
                      {user?.subStatus === 'active'
                        ? t('settings.renewOrExtend', 'Renew or extend')
                        : t('settings.choosePlan', 'Choose a plan')}
                    </Text>
                    {loadingBundles ? <ActivityIndicator size="small" color={colors.primary} /> : null}
                  </View>

                  {loadingBundles ? (
                    <Text style={s.planEmptyText}>{t('settings.loadingPlans', 'Loading plans...')}</Text>
                  ) : subscriptionBundles.length > 0 ? (
                    <View style={s.planList}>
                      {subscriptionBundles.map((bundle) => {
                        const busy = startingBundleId === bundle.id;
                        const dimmed = startingBundleId !== null && !busy;
                        return (
                          <TouchableOpacity
                            key={bundle.id}
                            onPress={() => handlePlanPress(bundle)}
                            accessibilityRole="button"
                            accessibilityLabel={`${planAction} ${bundle.label}`}
                            style={[s.planCard, dimmed && s.planCardDisabled]}
                            disabled={startingBundleId !== null}
                          >
                            <View style={{ flex: 1 }}>
                              <Text style={s.planName}>{bundle.label}</Text>
                              <Text style={s.planMeta}>{formatBundleDuration(bundle.durationMonths, t)}</Text>
                            </View>
                            <Text style={s.planPrice}>${bundle.priceUsd.toFixed(2)}</Text>
                            <View style={s.planActionChip}>
                              {busy ? (
                                <ActivityIndicator size="small" color={colors.primary} />
                              ) : (
                                <>
                                  <Text style={s.planActionChipText}>{planAction}</Text>
                                  <Ionicons name="chevron-forward" size={14} color={colors.primary} />
                                </>
                              )}
                            </View>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  ) : (
                    <TouchableOpacity
                      onPress={() => Linking.openURL('https://app.fishmemory.net/subscribe')}
                      accessibilityRole="button"
                      accessibilityLabel={t('settings.openSubscribe', 'Open subscribe page')}
                      style={s.planEmptyAction}
                    >
                      <Text style={s.planEmptyActionText}>{t('settings.openSubscribe', 'Open subscribe page')}</Text>
                      <Ionicons name="open-outline" size={16} color={colors.primary} />
                    </TouchableOpacity>
                  )}

                  {/* Trust line / graceful degradation */}
                  {gateway ? (
                    <View style={s.trustLine}>
                      <Ionicons name="alert-circle-outline" size={13} color={colors.textDim} />
                      <Text style={s.trustLineText}>{gateway.message}</Text>
                    </View>
                  ) : (
                    <View style={s.trustLine}>
                      <Ionicons name="lock-closed" size={12} color={colors.textDim} />
                      <Text style={s.trustLineText}>
                        {getPaymentTrustLine(cardEnabled, whishEnabled, visiblePaymentMethodCount, t)}
                      </Text>
                    </View>
                  )}
                </View>

                {hasManagement && (
                  <View style={s.subGroup}>
                    <Text style={s.subGroupLabel}>{t('settings.savedPaymentMethod', 'Saved payment method')}</Text>

                    {subscriptionDetails!.card ? (
                      <>
                        <View style={s.cardOnFile}>
                          <View style={s.cardOnFileIcon}>
                            <Ionicons name="card" size={20} color={colors.primary} />
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text style={s.cardOnFileTitle}>
                              {`${subscriptionDetails!.card.brand} ···· ${subscriptionDetails!.card.last4}`}
                            </Text>
                            <Text style={s.cardOnFileMeta}>
                              {t('settings.cardExpiry', {
                                defaultValue: 'Expires {{month}}/{{year}}',
                                month: String(subscriptionDetails!.card.expMonth).padStart(2, '0'),
                                year: subscriptionDetails!.card.expYear,
                              })}
                            </Text>
                          </View>
                        </View>

                        <View style={s.cardActionsRow}>
                          <TouchableOpacity
                            style={s.cardActionBtn}
                            onPress={handleUpdateCard}
                            accessibilityRole="button"
                            accessibilityLabel={t('settings.updateCard', 'Update card')}
                          >
                            <Ionicons name="pencil-outline" size={15} color={colors.primary} />
                            <Text style={s.cardActionBtnText}>{t('settings.updateCard', 'Update card')}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[s.cardActionBtn, s.cardActionBtnDanger]}
                            onPress={handleRemoveCard}
                            disabled={removeCardBusy}
                            accessibilityRole="button"
                            accessibilityLabel={t('settings.removeCard', 'Remove card')}
                          >
                            {removeCardBusy ? (
                              <ActivityIndicator size="small" color={colors.error} />
                            ) : (
                              <Ionicons name="trash-outline" size={15} color={colors.error} />
                            )}
                            <Text style={[s.cardActionBtnText, { color: colors.error }]}>
                              {t('settings.removeCard', 'Remove card')}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </>
                    ) : (
                      <TouchableOpacity
                        style={s.addCardCta}
                        onPress={handleUpdateCard}
                        accessibilityRole="button"
                        accessibilityLabel={t('settings.addCard', 'Add a card')}
                      >
                        <View style={s.addCardIcon}>
                          <Ionicons name="add" size={22} color={colors.primary} />
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={s.addCardTitle}>{t('settings.addCard', 'Add a card')}</Text>
                          <Text style={s.addCardDesc}>
                            {t('settings.addCardDesc', 'Save a card to enable auto-renew and one-tap extend.')}
                          </Text>
                        </View>
                        <Ionicons name="chevron-forward" size={18} color={colors.primary} />
                      </TouchableOpacity>
                    )}

                    {subscriptionDetails!.card && (
                      <View style={s.autoRenewRow}>
                        <View style={{ flex: 1, marginRight: SPACING.md }}>
                          <Text style={s.autoRenewLabel}>{t('settings.autoRenew', 'Auto-renew')}</Text>
                          <Text style={s.autoRenewDesc}>
                            {subscriptionDetails!.autoRenew && subscriptionDetails!.nextRenewalAtUtc
                              ? t('settings.nextCharge', {
                                  defaultValue: 'Next charge: {{date}}',
                                  date: formatDisplayDate(subscriptionDetails!.nextRenewalAtUtc),
                                })
                              : t('settings.autoRenewDesc', 'Automatically renew your subscription before it expires.')}
                          </Text>
                        </View>
                        {autoRenewBusy ? (
                          <ActivityIndicator size="small" color={colors.primary} />
                        ) : (
                          <Switch
                            value={subscriptionDetails!.autoRenew}
                            onValueChange={handleToggleAutoRenew}
                            trackColor={{ false: colors.border, true: colors.primary }}
                          />
                        )}
                      </View>
                    )}

                    {subscriptionDetails!.subStatus === 'active' && (
                      <TouchableOpacity
                        style={s.cancelLink}
                        onPress={handleCancelSubscription}
                        disabled={cancelBusy}
                        accessibilityRole="button"
                        accessibilityLabel={t('settings.cancelSubscription', 'Cancel subscription')}
                      >
                        <Text style={s.cancelLinkText}>
                          {t('settings.cancelSubscription', 'Cancel subscription')}
                        </Text>
                        {cancelBusy && <ActivityIndicator size="small" color={colors.error} />}
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </>
            );
          })()}
        </View>

        {/* App info */}
        <SectionHeader title={t('settings.about')} />
        <View style={s.card}>
          <View style={s.settingRow}>
            <Text style={s.settingLabel}>{t('settings.version')}</Text>
            <Text style={s.settingValue}>{APP_VERSION}</Text>
          </View>
        </View>

        {/* Feedback & Support */}
        <TouchableOpacity style={[s.card, { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14 }]} onPress={handleFeedback}>
          <Ionicons name="chatbubble-ellipses-outline" size={18} color={colors.primary} />
          <Text style={[s.settingLabel, { flex: 1 }]}>{t('settings.feedback', 'Send Feedback')}</Text>
          <Ionicons name="chevron-forward" size={16} color={colors.textSecondary} />
        </TouchableOpacity>

        {/* Chat with Support */}
        <TouchableOpacity style={[s.card, { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14 }]} onPress={() => router.push('/support-chat')}>
          <Ionicons name="headset-outline" size={18} color={colors.primary} />
          <Text style={[s.settingLabel, { flex: 1 }]}>Chat with Support</Text>
          <Ionicons name="chevron-forward" size={16} color={colors.textSecondary} />
        </TouchableOpacity>

        {/* Data Export */}
        <TouchableOpacity style={[s.card, { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14 }]} onPress={handleExportData} disabled={exportingData}>
          <Ionicons name="download-outline" size={18} color={colors.primary} />
          <Text style={[s.settingLabel, { flex: 1 }]}>{t('settings.exportData', 'Export My Data')}</Text>
          {exportingData ? <ActivityIndicator size="small" color={colors.primary} /> : <Ionicons name="chevron-forward" size={16} color={colors.textSecondary} />}
        </TouchableOpacity>

        {/* Active Sessions */}
        <SectionHeader title={t('settings.sessions', 'Active Sessions')} />
        <View style={s.card}>
          <TouchableOpacity style={[s.settingRow, { justifyContent: 'center', paddingVertical: 10 }]} onPress={loadDevices}>
            {loadingDevices ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <Text style={{ color: colors.primary, fontWeight: '600', fontSize: 14 }}>
                {activeDevices.length > 0 ? t('settings.refreshDevices', '↻ Refresh') : t('settings.loadDevices', 'Load Active Devices')}
              </Text>
            )}
          </TouchableOpacity>
          {activeDevices.map((d) => (
            <View key={d.device_id} style={[s.settingRow, { gap: 8 }]}>
              <Ionicons
                name={d.platform?.toLowerCase().includes('ios') || d.platform?.toLowerCase().includes('iphone') ? 'logo-apple' : d.platform?.toLowerCase().includes('android') ? 'logo-android' : 'globe-outline'}
                size={18}
                color={colors.textSecondary}
              />
              <View style={{ flex: 1 }}>
                <Text style={s.settingLabel}>{d.platform || 'Unknown'}</Text>
                <Text style={{ fontSize: 11, color: colors.textSecondary }}>{d.app_version || ''} — {d.last_seen_utc ? new Date(d.last_seen_utc).toLocaleDateString() : ''}</Text>
              </View>
            </View>
          ))}
          {activeDevices.length > 1 && (
            <TouchableOpacity style={[s.settingRow, { justifyContent: 'center', paddingVertical: 10 }]} onPress={handleLogoutOthers}>
              <Text style={{ color: colors.danger, fontWeight: '600', fontSize: 14 }}>
                {t('settings.logoutOthers', 'Log Out All Other Devices')}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Security — Biometric Lock */}
        {biometricAvail && (
          <>
            <SectionHeader title={t('settings.security', 'Security')} />
            <View style={s.card}>
              <View style={s.settingRow}>
                <Ionicons name={biometricType === 'face' ? 'scan-outline' : 'finger-print-outline'} size={20} color={colors.primary} />
                <Text style={s.settingLabel}>
                  {biometricType === 'face'
                    ? t('settings.faceIdLock', 'Face ID Lock')
                    : t('settings.fingerprintLock', 'Fingerprint Lock')}
                </Text>
                <Switch
                  value={biometricOn}
                  onValueChange={async (val) => {
                    await setBiometricEnabled(val);
                    setBiometricOn(val);
                  }}
                  trackColor={{ true: colors.primary }}
                />
              </View>
              <Text style={{ fontSize: 12, color: colors.textSecondary, paddingHorizontal: 12, paddingBottom: 10 }}>
                {t('settings.biometricDesc', 'Require authentication when opening the app')}
              </Text>
            </View>
          </>
        )}

        {/* Logout */}
        <TouchableOpacity style={s.logoutButton} onPress={handleLogout}>
          <Ionicons name="log-out-outline" size={18} color={colors.danger} />
          <Text style={s.logoutText}>{t('settings.logout')}</Text>
        </TouchableOpacity>

        {/* Danger Zone — Delete Account */}
        <SectionHeader title={t('settings.dangerZone', 'Danger Zone')} />
        {user?.scheduledDeletionAt ? (
          <View style={[s.card, { borderColor: colors.danger, borderWidth: 1, padding: 14 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Ionicons name="time-outline" size={18} color={colors.danger} />
              <Text style={[s.settingLabel, { color: colors.danger }]}>{t('settings.deletionScheduled', 'Deletion Scheduled')}</Text>
            </View>
            <Text style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 12 }}>
              {t('settings.deletionScheduledDesc', 'Your account will be permanently deleted on {{date}}. You can cancel before then, and support can see this request.', { date: new Date(user.scheduledDeletionAt).toLocaleDateString() })}
            </Text>
            <TouchableOpacity
              style={{ backgroundColor: colors.primary, borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
              onPress={handleCancelDeletion}
            >
              <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>{t('settings.cancelDeletion', 'Cancel Deletion')}</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity style={[s.card, { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14, borderColor: colors.danger, borderWidth: 1, opacity: deletingAccount ? 0.6 : 1 }]} onPress={handleDeleteAccount} disabled={deletingAccount}>
            {deletingAccount ? <ActivityIndicator size="small" color={colors.danger} /> : <Ionicons name="trash-outline" size={18} color={colors.danger} />}
            <View style={{ flex: 1 }}>
              <Text style={[s.settingLabel, { color: colors.danger }]}>{deletingAccount ? t('settings.deleting', 'Deleting…') : t('settings.deleteAccount', 'Delete Account')}</Text>
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>{t('settings.deleteAccountDesc', 'Schedule account for deletion and notify support')}</Text>
            </View>
          </TouchableOpacity>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Extend: choose saved card or a new card */}
      <Modal
        visible={!!extendChoiceBundle}
        transparent
        animationType="fade"
        onRequestClose={() => setExtendChoiceBundle(null)}
      >
        <View style={s.choiceOverlay}>
          <View style={s.choiceCard}>
            <Text style={s.choiceTitle}>
              {t('settings.extendChoiceTitle', { defaultValue: 'Extend {{plan}}', plan: extendChoiceBundle?.label })}
            </Text>
            {extendChoiceBundle && (
              <Text style={s.choiceSubtitle}>{`$${extendChoiceBundle.priceUsd.toFixed(2)}`}</Text>
            )}
            {!!subscriptionDetails?.card && (
              <TouchableOpacity
                style={[s.choiceBtn, s.choiceBtnPrimary]}
                accessibilityRole="button"
                onPress={() => { if (extendChoiceBundle) handleExtendWithSavedCard(extendChoiceBundle); }}
              >
                <Ionicons name="card" size={18} color="#fff" />
                <Text style={s.choiceBtnPrimaryText}>
                  {t('settings.payWithSavedCard', {
                    defaultValue: 'Pay with {{brand}} ····{{last4}}',
                    brand: subscriptionDetails.card.brand,
                    last4: subscriptionDetails.card.last4,
                  })}
                </Text>
              </TouchableOpacity>
            )}
            {!!paymentStatus?.enabled && (
              <TouchableOpacity
                style={[s.choiceBtn, subscriptionDetails?.card ? s.choiceBtnSecondary : s.choiceBtnPrimary]}
                accessibilityRole="button"
                onPress={() => {
                  const bundle = extendChoiceBundle;
                  setExtendChoiceBundle(null);
                  if (bundle) handleStartCheckout(bundle.id);
                }}
              >
                <Ionicons
                  name={subscriptionDetails?.card ? 'add-outline' : 'card'}
                  size={18}
                  color={subscriptionDetails?.card ? colors.primary : '#fff'}
                />
                <Text style={subscriptionDetails?.card ? s.choiceBtnSecondaryText : s.choiceBtnPrimaryText}>
                  {subscriptionDetails?.card
                    ? t('settings.useDifferentCard', 'Use a different card')
                    : t('settings.payWithCard', 'Pay with card')}
                </Text>
              </TouchableOpacity>
            )}
            {whishEnabled && (
              <TouchableOpacity
                style={[s.choiceBtn, s.choiceBtnSecondary]}
                accessibilityRole="button"
                onPress={() => {
                  const bundle = extendChoiceBundle;
                  if (bundle) handleStartWhish(bundle);
                }}
              >
                <WhishMark size="sm" variant="outline" />
                <Text style={s.choiceBtnSecondaryText}>
                  {t('settings.payWithWhish', 'Pay with Whish wallet')}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={s.choiceCancel}
              accessibilityRole="button"
              onPress={() => setExtendChoiceBundle(null)}
            >
              <Text style={s.choiceCancelText}>{t('common.cancel', 'Cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* In-app confirmation dialog */}
      <Modal
        visible={!!confirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => setConfirmModal(null)}
      >
        <View style={s.choiceOverlay}>
          <View style={s.choiceCard}>
            <Text style={s.choiceTitle}>{confirmModal?.title}</Text>
            <Text style={s.choiceSubtitle}>{confirmModal?.message}</Text>
            <TouchableOpacity
              style={[s.choiceBtn, confirmModal?.danger ? s.choiceBtnDanger : s.choiceBtnPrimary]}
              accessibilityRole="button"
              onPress={() => {
                const action = confirmModal?.onConfirm;
                setConfirmModal(null);
                action?.();
              }}
            >
              <Text style={confirmModal?.danger ? s.choiceBtnDangerText : s.choiceBtnPrimaryText}>
                {confirmModal?.confirmLabel}
              </Text>
            </TouchableOpacity>
            {confirmModal?.secondaryLabel ? (
              <TouchableOpacity
                style={[s.choiceBtn, confirmModal?.secondaryDanger ? s.choiceBtnDanger : s.choiceBtnPrimary, { marginTop: 8 }]}
                accessibilityRole="button"
                onPress={() => {
                  const action = confirmModal?.onSecondary;
                  setConfirmModal(null);
                  action?.();
                }}
              >
                <Text style={confirmModal?.secondaryDanger ? s.choiceBtnDangerText : s.choiceBtnPrimaryText}>
                  {confirmModal.secondaryLabel}
                </Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={s.choiceCancel}
              accessibilityRole="button"
              onPress={() => setConfirmModal(null)}
            >
              <Text style={s.choiceCancelText}>{t('common.cancel', 'Cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Notification History Modal */}
      <Modal visible={showNotifHistory} animationType="slide" onRequestClose={() => setShowNotifHistory(false)}>
        <View style={{ flex: 1, backgroundColor: colors.background, paddingTop: insets.top }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 0.5, borderBottomColor: colors.border }}>
            <Text style={{ fontSize: 18, fontWeight: '700', color: colors.text }}>{t('settings.notifHistory', 'Notification History')}</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              {notifHistoryNotifications.length > 0 && (
                <TouchableOpacity onPress={() => useNotificationHistory.getState().markAllRead()}>
                  <Text style={{ color: colors.primary, fontSize: 14 }}>{t('common.markAllRead', 'Mark All Read')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={() => setShowNotifHistory(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
          </View>
          <FlatList
            data={notifHistoryNotifications}
            keyExtractor={item => item.id}
            ListEmptyComponent={
              <View style={{ alignItems: 'center', padding: 40 }}>
                <Ionicons name="notifications-off-outline" size={48} color={colors.textSecondary} />
                <Text style={{ color: colors.textSecondary, marginTop: 12 }}>{t('settings.noNotifications', 'No notifications yet')}</Text>
              </View>
            }
            renderItem={({ item }) => (
              <TouchableOpacity
                style={{ padding: 16, borderBottomWidth: 0.5, borderBottomColor: colors.border, backgroundColor: item.read ? 'transparent' : colors.primary + '08' }}
                onPress={() => useNotificationHistory.getState().markRead(item.id)}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Ionicons
                    name={item.type === 'reminder' ? 'alarm-outline' : item.type === 'message' ? 'chatbubble-outline' : 'information-circle-outline'}
                    size={18}
                    color={item.read ? colors.textSecondary : colors.primary}
                  />
                  <Text style={{ fontWeight: item.read ? '400' : '600', color: colors.text, flex: 1 }}>{item.title}</Text>
                  {!item.read && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary }} />}
                </View>
                <Text style={{ color: colors.textSecondary, marginTop: 4, fontSize: 14 }} numberOfLines={2}>{item.body}</Text>
                <Text style={{ color: colors.textSecondary, fontSize: 11, marginTop: 4 }}>{new Date(item.timestamp).toLocaleString()}</Text>
              </TouchableOpacity>
            )}
          />
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

function SectionHeader({ title }: { title: string }) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  return <Text style={s.sectionHeader}>{title}</Text>;
}

function getPlanActionLabel(baseAction: string, cardEnabled: boolean, whishEnabled: boolean, t: any) {
  if (cardEnabled && whishEnabled) return t('settings.choosePayment', 'Choose payment');
  if (whishEnabled) return t('settings.payWithWhishShort', 'Pay with Whish');
  if (cardEnabled) return t('settings.payByCardShort', 'Pay by card');
  return baseAction;
}

function getPaymentTrustLine(cardEnabled: boolean, whishEnabled: boolean, methodCount: number, t: any) {
  if (methodCount > 1) return t('settings.payByCardOrWhish', 'Pay securely by card or Whish.');
  if (whishEnabled) return t('settings.paySecurelyWithWhish', 'Pay securely with Whish wallet.');
  if (cardEnabled) return t('settings.cardSecuredByCybersource', 'Card payments secured by CyberSource.');
  return t('settings.openSubscribeForPayment', 'Open the subscribe page to see available payment options.');
}

function getSubscriptionSummary(user: User | null, t: any, colors: ThemeColors) {
  const status = user?.subStatus || (user?.trialEndUtc ? 'trial' : 'unsubscribed');
  const paidThrough = formatDisplayDate(user?.subUntilUtc);
  const trialEnd = formatDisplayDate(user?.trialEndUtc);
  const trialDaysLeft = user?.trialEndUtc
    ? Math.max(0, Math.ceil((new Date(user.trialEndUtc).getTime() - Date.now()) / 86400000))
    : null;

  if (status === 'active') {
    return {
      icon: 'checkmark-circle',
      iconColor: colors.success,
      iconBg: 'rgba(52, 168, 83, 0.12)',
      pillBg: 'rgba(52, 168, 83, 0.12)',
      pillBorder: 'rgba(52, 168, 83, 0.24)',
      pillText: colors.success,
      statusLabel: t('settings.activeStatusLabel', 'Active'),
      title: t('settings.subscriptionActiveTitle', 'Premium is active'),
      description: paidThrough
        ? t('settings.subscriptionActiveUntil', { defaultValue: 'Active until {{date}}', date: paidThrough })
        : t('settings.subscriptionActiveNoDate', 'Your premium access is active.'),
    };
  }

  if (status === 'expired') {
    return {
      icon: 'alert-circle-outline',
      iconColor: colors.error,
      iconBg: 'rgba(192, 57, 43, 0.12)',
      pillBg: 'rgba(192, 57, 43, 0.12)',
      pillBorder: 'rgba(192, 57, 43, 0.24)',
      pillText: colors.error,
      statusLabel: t('settings.expiredStatusLabel', 'Expired'),
      title: t('settings.subscriptionExpiredTitle', 'Subscription expired'),
      description: paidThrough
        ? t('settings.subscriptionExpiredOn', { defaultValue: 'Ended {{date}}', date: paidThrough })
        : t('settings.subscriptionExpiredDesc', 'Choose a plan to reactivate premium access.'),
    };
  }

  if (user?.trialEndUtc || status === 'trial') {
    return {
      icon: 'time-outline',
      iconColor: colors.warning,
      iconBg: 'rgba(255, 149, 0, 0.12)',
      pillBg: 'rgba(255, 149, 0, 0.12)',
      pillBorder: 'rgba(255, 149, 0, 0.24)',
      pillText: colors.warning,
      statusLabel: t('settings.trialStatusLabel', 'Trial'),
      title: trialDaysLeft === null
        ? t('settings.trialStatus', 'Trial available')
        : t('settings.trialDays', { daysLeft: trialDaysLeft }),
      description: trialEnd
        ? t('settings.trialEnds', { defaultValue: 'Trial ends {{date}}', date: trialEnd })
        : t('settings.trialStatus', 'Trial available'),
    };
  }

  return {
    icon: 'lock-closed-outline',
    iconColor: colors.textSecondary,
    iconBg: colors.surfaceTinted,
    pillBg: colors.surfaceTinted,
    pillBorder: colors.border,
    pillText: colors.textSecondary,
    statusLabel: t('settings.unsubscribedStatusLabel', 'No plan'),
    title: t('settings.subscriptionInactiveTitle', 'No active subscription'),
    description: t('settings.subscriptionInactiveDesc', 'Choose a plan to activate premium access.'),
  };
}

// Returns a non-null value only when online checkout is genuinely unavailable,
// so the UI can degrade gracefully. Otherwise the visible trust line describes
// the currently available checkout methods.
function getGatewaySummary(paymentStatus: PaymentProviderStatus | null, failed: boolean, whishEnabled: boolean, t: any) {
  if (failed) {
    return {
      message: t('settings.paymentStatusUnavailableDesc', 'Plans could not be loaded from the server.'),
    };
  }

  if (paymentStatus && !paymentStatus.enabled && !whishEnabled) {
    return {
      message: t('settings.onlinePaymentUnavailableDesc', 'Online checkout is unavailable right now.'),
    };
  }

  return null;
}

function formatDisplayDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

function getTimeInputParts(minutes: number): { hour: string; minute: string; period: 'AM' | 'PM' } {
  const clamped = Math.max(0, Math.min(1439, Math.round(minutes)));
  const hour24 = Math.floor(clamped / 60);
  const minute = clamped % 60;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return { hour: String(hour12), minute: String(minute).padStart(2, '0'), period };
}

function parseTimeInput(hourText: string, minuteText: string, period: 'AM' | 'PM') {
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isInteger(hour) || hour < 1 || hour > 12) return null;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  const hour24 = period === 'PM'
    ? (hour === 12 ? 12 : hour + 12)
    : (hour === 12 ? 0 : hour);
  return hour24 * 60 + minute;
}

function sanitizeNumberInput(value: string, maxLength: number) {
  return value.replace(/[^0-9]/g, '').slice(0, maxLength);
}

function formatTimeOfDay(minutes: number) {
  const clamped = Math.max(0, Math.min(1439, Math.round(minutes)));
  const date = new Date(2000, 0, 1, Math.floor(clamped / 60), clamped % 60);
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
}

function withQueryParams(url: string, params: Record<string, string>) {
  try {
    const target = new URL(url);
    Object.entries(params).forEach(([key, value]) => target.searchParams.set(key, value));
    return target.toString();
  } catch {
    const qs = new URLSearchParams(params).toString();
    return `${url}${url.includes('?') ? '&' : '?'}${qs}`;
  }
}

function formatBundleDuration(months: number, t: any) {
  if (months === 1) return t('settings.oneMonth', '1 month');
  return t('settings.monthCount', { defaultValue: '{{count}} months', count: months });
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 40,
  },
  title: {
    fontSize: FONT_SIZE.xl,
    fontFamily: 'Inter_400Regular',
    color: colors.text,
    marginBottom: SPACING.lg,
    paddingHorizontal: SPACING.xs,
    letterSpacing: 0.3,
  },
  heroCard: {
    marginHorizontal: -2,
    paddingHorizontal: 22,
    paddingTop: 18,
    paddingBottom: 22,
    borderRadius: 28,
    backgroundColor: '#0D47A1',
    overflow: 'hidden',
    shadowColor: '#0a1628',
    shadowOpacity: 0.35,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 16 },
    elevation: 12,
  },
  heroOrbPrimary: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: 'rgba(100, 170, 255, 0.12)',
    top: -60,
    right: -50,
  },
  heroOrbSecondary: {
    position: 'absolute',
    width: 140,
    height: 140,
    borderRadius: 70,
    backgroundColor: 'rgba(212, 165, 116, 0.09)',
    bottom: -50,
    left: -30,
  },
  heroTitle: {
    fontSize: FONT_SIZE.xl,
    fontFamily: 'Inter_400Regular',
    color: '#fff',
    letterSpacing: 0.5,
  },
  heroSubtitle: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_400Regular',
    color: 'rgba(255,255,255,0.50)',
    marginTop: SPACING.xs,
    letterSpacing: 0.2,
  },
  sectionHeader: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_600SemiBold',
    color: colors.textDim,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginTop: SPACING.xl,
    marginBottom: SPACING.sm,
    paddingHorizontal: SPACING.xs,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    borderWidth: 0.5,
    borderColor: colors.border,
    overflow: 'hidden',
    shadowColor: 'rgba(0,0,0,0.06)',
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
    elevation: 2,
  },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: SPACING.base,
    gap: SPACING.md,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: RADIUS.xl,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: '#fff',
    fontSize: FONT_SIZE.lg,
    fontWeight: '300',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_500Medium',
    color: colors.text,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  editNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  nameInput: {
    flex: 1,
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
    borderBottomWidth: 1,
    borderBottomColor: colors.primary,
    paddingVertical: 4,
  },
  saveNameBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveNameText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  profilePhone: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  languageDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginHorizontal: 16,
  },
  langTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  langTriggerFlag: {
    fontSize: FONT_SIZE.lg,
  },
  langTriggerLabel: {
    fontSize: FONT_SIZE.sm,
    color: colors.text,
    fontWeight: '500',
    maxWidth: 120,
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: SPACING.base,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.border,
  },
  inputSection: {
    paddingHorizontal: SPACING.base,
    paddingBottom: SPACING.base,
    gap: SPACING.md,
  },
  readonlyField: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: SPACING.base,
    marginBottom: SPACING.base,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.md,
    backgroundColor: colors.surfaceTinted,
    borderWidth: 0.5,
    borderColor: colors.border,
  },
  readonlyFieldTextWrap: {
    flex: 1,
    marginRight: SPACING.md,
  },
  readonlyFieldValue: {
    fontSize: FONT_SIZE.sm,
    color: colors.text,
  },
  readonlyFieldHint: {
    marginTop: SPACING.xs,
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
  },
  intervalRow: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  intervalChip: {
    paddingHorizontal: SPACING.md,
    paddingVertical: 6,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.surfaceTinted,
  },
  intervalChipActive: {
    backgroundColor: colors.primaryLight,
    borderWidth: 0.5,
    borderColor: colors.primary,
  },
  intervalText: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '500',
    color: colors.textSecondary,
  },
  intervalTextActive: {
    color: colors.primary,
    fontWeight: '700',
  },
  settingInfo: {
    flex: 1,
    marginRight: 12,
  },
  settingLabel: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_500Medium',
    color: colors.text,
  },
  settingDesc: {
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  settingValue: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 10,
  },
  secondaryButton: {
    flex: 1,
    minHeight: 44,
    borderRadius: RADIUS.md,
    borderWidth: 0.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: colors.text,
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
  },
  connectButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: SPACING.base,
    paddingVertical: 9,
    borderRadius: RADIUS.md,
  },
  connectButtonText: {
    color: '#fff',
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
  },
  // ── Subscription: status header ──
  subStatusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    padding: SPACING.base,
  },
  subStatusIcon: {
    width: 42,
    height: 42,
    borderRadius: RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  subStatusTitle: {
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
  },
  subStatusSubtitle: {
    marginTop: 3,
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
  },
  statusPill: {
    minHeight: 26,
    paddingHorizontal: 10,
    borderRadius: RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusPillText: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_600SemiBold',
  },
  // ── Subscription: past-due banner ──
  renewalBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    marginHorizontal: SPACING.base,
    marginBottom: SPACING.base,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.md,
    borderWidth: 0.5,
    borderColor: 'rgba(192, 57, 43, 0.24)',
    backgroundColor: 'rgba(192, 57, 43, 0.12)',
  },
  renewalBannerText: {
    flex: 1,
    fontSize: FONT_SIZE.xs,
    color: colors.error,
  },
  // ── Subscription: grouped subsections ──
  subGroup: {
    paddingHorizontal: SPACING.base,
    paddingTop: SPACING.base,
    paddingBottom: SPACING.base,
    borderTopWidth: 0.5,
    borderTopColor: colors.border,
  },
  subGroupLabel: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_600SemiBold',
    color: colors.textDim,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: SPACING.md,
  },
  // ── Subscription: card on file ──
  cardOnFile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    padding: SPACING.md,
    borderRadius: RADIUS.md,
    backgroundColor: colors.surfaceTinted,
  },
  cardOnFileIcon: {
    width: 38,
    height: 38,
    borderRadius: RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
  },
  cardOnFileTitle: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
  },
  cardOnFileMeta: {
    marginTop: 2,
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
  },
  cardActionsRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
    marginTop: SPACING.sm,
  },
  cardActionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    minHeight: 40,
    borderRadius: RADIUS.md,
    borderWidth: 0.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    paddingHorizontal: SPACING.sm,
  },
  cardActionBtnDanger: {
    borderColor: 'rgba(192, 57, 43, 0.25)',
  },
  cardActionBtnText: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.primary,
  },
  // ── Subscription: add card CTA ──
  addCardCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    padding: SPACING.md,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.surfaceTinted,
  },
  addCardIcon: {
    width: 38,
    height: 38,
    borderRadius: RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
  },
  addCardTitle: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.primary,
  },
  addCardDesc: {
    marginTop: 2,
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
  },
  // ── Subscription: auto-renew ──
  autoRenewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: 0.5,
    borderTopColor: colors.border,
  },
  autoRenewLabel: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
  },
  autoRenewDesc: {
    marginTop: 2,
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
  },
  // ── Subscription: cancel link ──
  cancelLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
    minHeight: 40,
    marginTop: SPACING.sm,
  },
  cancelLinkText: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_500Medium',
    color: colors.error,
  },
  // ── Subscription: plans ──
  planHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  planList: {
    gap: SPACING.sm,
  },
  planCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    minHeight: 58,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.md,
    borderWidth: 0.5,
    borderColor: colors.border,
    backgroundColor: colors.surfaceTinted,
  },
  planCardDisabled: {
    opacity: 0.5,
  },
  planActionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    minWidth: 64,
    minHeight: 30,
    paddingHorizontal: 10,
    borderRadius: RADIUS.full,
    backgroundColor: colors.surface,
    borderWidth: 0.5,
    borderColor: colors.border,
    justifyContent: 'center',
  },
  planActionChipText: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_600SemiBold',
    color: colors.primary,
  },
  // ── Subscription: trust line ──
  trustLine: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    marginTop: SPACING.base,
  },
  trustLineText: {
    fontSize: FONT_SIZE.xs,
    color: colors.textDim,
  },
  choiceOverlay: {
    flex: 1,
    backgroundColor: 'rgba(8, 15, 30, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.lg,
  },
  choiceCard: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: colors.surface,
    borderRadius: RADIUS.xl,
    padding: SPACING.lg,
    gap: SPACING.sm,
  },
  choiceTitle: {
    fontSize: FONT_SIZE.lg,
    fontFamily: 'Inter_700Bold',
    color: colors.text,
    textAlign: 'center',
  },
  choiceSubtitle: {
    fontSize: FONT_SIZE.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  choiceBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
    minHeight: 50,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.md,
  },
  choiceBtnPrimary: {
    backgroundColor: colors.primary,
  },
  choiceBtnPrimaryText: {
    fontSize: FONT_SIZE.md,
    fontFamily: 'Inter_700Bold',
    color: '#fff',
  },
  choiceBtnDanger: {
    backgroundColor: colors.error,
  },
  choiceBtnDangerText: {
    fontSize: FONT_SIZE.md,
    fontFamily: 'Inter_700Bold',
    color: '#fff',
  },
  choiceBtnSecondary: {
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  choiceBtnSecondaryText: {
    fontSize: FONT_SIZE.md,
    fontFamily: 'Inter_400Regular',
    color: colors.primary,
  },
  choiceCancel: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  choiceCancelText: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
  },
  planName: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
  },
  planMeta: {
    marginTop: 2,
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
  },
  planPrice: {
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_700Bold',
    color: colors.text,
  },
  planEmptyText: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
  },
  planEmptyAction: {
    minHeight: 48,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.md,
    borderWidth: 0.5,
    borderColor: colors.border,
    backgroundColor: colors.surfaceTinted,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  planEmptyActionText: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.primary,
  },
  logoutButton: {
    marginTop: SPACING['2xl'],
    padding: SPACING.base,
    backgroundColor: colors.surface,
    borderRadius: RADIUS.xl,
    flexDirection: 'row',
    gap: SPACING.sm,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0.5,
    borderColor: 'rgba(192, 57, 43, 0.25)',
    shadowColor: 'rgba(0,0,0,0.06)',
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
    elevation: 1,
  },
  logoutText: {
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_500Medium',
    color: colors.error,
  },
  defaultTimeEditorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.base,
    paddingBottom: SPACING.base,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.border,
    flexWrap: 'wrap',
  },
  timeInputGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.sm,
    height: 42,
  },
  timeNumberInput: {
    width: 42,
    height: 38,
    textAlign: 'center',
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.text,
    paddingVertical: 0,
  },
  timeSeparator: {
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  periodToggle: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: RADIUS.md,
    overflow: 'hidden',
    backgroundColor: colors.background,
    height: 42,
  },
  periodOption: {
    width: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  periodOptionActive: {
    backgroundColor: colors.primary + '18',
  },
  periodText: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  periodTextActive: {
    color: colors.primary,
  },
  timeSaveButton: {
    width: 42,
    height: 42,
    borderRadius: RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  timeSaveButtonDisabled: {
    opacity: 0.6,
  },
  defaultTimePreviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: SPACING.base,
    paddingBottom: SPACING.base,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.border,
  },
  defaultTimePreviewText: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  durationChip: {
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background,
  },
  durationChipActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '18',
  },
  durationChipText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '500',
    color: colors.textSecondary,
  },
  durationChipTextActive: {
    color: colors.primary,
    fontWeight: '700',
  },
  themeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.md,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: 'transparent',
    marginBottom: SPACING.xs,
  },
  themeOptionText: {
    fontSize: FONT_SIZE.base,
    color: colors.text,
  },
});
